import * as React from 'react';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { withTranslation } from 'react-i18next';
import CustomerDialog from './CustomerDialog';
import CustomerDetailsDialog from './CustomerDetailsDialog';
import { formatDate } from '../../utils';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import PaginationControls from '../shared/PaginationControls';
import {
  useListPreferences,
  FilterButton,
  FilterPanel,
  FilterSection,
  FilterChip,
  SearchInput,
  FilterSummary,
} from '../shared/ListFilters';
import DataTable from '../shared/DataTable';

const STORAGE_KEY = 'customers_list_preferences';

const defaultPreferences = {
  sortField: 'name',
  sortDirection: 'asc',
  searchQuery: '',
  filters: {
    type: '',         // '' | 'company' | 'individual'
    has_email: '',    // '' | true | false
    has_phone: '',    // '' | true | false
  },
};

const MainCustomersView = ({ t }) => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep link from the dashboard (search widget, top customers): /customers?customer=<id>.
  useEffect(() => {
    const customerId = searchParams.get('customer');
    if (!customerId) return;
    setSelectedCustomerId(Number(customerId));
    setDetailsDialogOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('customer');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('customer')]);

  // Use localStorage-persisted preferences
  const {
    preferences,
    updatePreferences,
    updateFilters: updateLocalFilters,
    resetFilters: resetLocalFilters,
    filtersOpen,
    toggleFiltersOpen,
  } = useListPreferences(STORAGE_KEY, defaultPreferences);

  // Extract filter values from preferences
  const filters = preferences.filters;

  // Use server-side pagination hook
  const {
    data: customers,
    loading,
    error,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
    searchQuery,
    setSearchQuery,
    setFilters: setServerFilters,
    sortField,
    sortDirection,
    handleSort,
    refetch,
  } = usePaginatedList('/api/customers/', {
    initialFilters: filters,
    defaultSortField: preferences.sortField,
    defaultSortDirection: preferences.sortDirection,
    defaultPageSize: 50,
    initialSearchQuery: preferences.searchQuery || '',
  });

  // Sync local filters to server
  React.useEffect(() => {
    setServerFilters(filters);
  }, [filters, setServerFilters]);

  // Sync search query from localStorage preferences to hook
  React.useEffect(() => {
    setSearchQuery(preferences.searchQuery || '');
  }, [preferences.searchQuery, setSearchQuery]);

  // Wrapper functions for filter updates
  const updateFilters = useCallback((newFilters) => {
    updateLocalFilters(newFilters);
  }, [updateLocalFilters]);

  // Reset all filters (resetLocalFilters also clears searchQuery in preferences)
  const resetFilters = useCallback(() => {
    resetLocalFilters();
  }, [resetLocalFilters]);

  const addCreatedCustomer = useCallback(() => {
    // Refetch to get the updated list with the new customer
    refetch();
  }, [refetch]);

  const updateCustomer = useCallback(() => {
    // Refetch to get the updated list
    refetch();
  }, [refetch]);

  const handleDeleteCustomer = useCallback(() => {
    // Refetch to get the updated list after deletion
    refetch();
  }, [refetch]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.type) count++;
    if (filters.has_email !== '' && filters.has_email !== null && filters.has_email !== undefined) count++;
    if (filters.has_phone !== '' && filters.has_phone !== null && filters.has_phone !== undefined) count++;
    return count;
  }, [filters]);

  // Build filter summary chips
  const filterSummaryChips = useMemo(() => {
    const chips = [];

    if (filters.type === 'company') {
      chips.push({ key: 'type', label: t('Companies') });
    } else if (filters.type === 'individual') {
      chips.push({ key: 'type', label: t('Individuals') });
    }

    if (filters.has_email === true || filters.has_email === 'true') {
      chips.push({ key: 'email', label: t('Has email') });
    } else if (filters.has_email === false || filters.has_email === 'false') {
      chips.push({ key: 'email', label: t('No email') });
    }

    if (filters.has_phone === true || filters.has_phone === 'true') {
      chips.push({ key: 'phone', label: t('Has phone') });
    } else if (filters.has_phone === false || filters.has_phone === 'false') {
      chips.push({ key: 'phone', label: t('No phone') });
    }

    return chips;
  }, [filters, t]);

  // Filter toggle handlers - convert to boolean for server
  const handleTypeFilter = useCallback((type) => {
    updateFilters({ type: filters.type === type ? '' : type });
  }, [filters.type, updateFilters]);

  const handleEmailFilter = useCallback((value) => {
    const currentValue = filters.has_email === true || filters.has_email === 'true';
    const newValue = value === true;
    updateFilters({ has_email: currentValue === newValue ? '' : newValue });
  }, [filters.has_email, updateFilters]);

  const handlePhoneFilter = useCallback((value) => {
    const currentValue = filters.has_phone === true || filters.has_phone === 'true';
    const newValue = value === true;
    updateFilters({ has_phone: currentValue === newValue ? '' : newValue });
  }, [filters.has_phone, updateFilters]);

  // State for error snackbar - cleared when error changes to null
  const [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);
  const prevError = React.useRef(error);

  React.useEffect(() => {
    // Show snackbar when a new error appears
    if (error && error !== prevError.current) {
      setShowErrorSnackbar(true);
    }
    prevError.current = error;
  }, [error]);

  const handleCloseErrorSnackbar = () => {
    setShowErrorSnackbar(false);
  };

  return (
    <Box data-testid="customers-page">
      {/* Error Snackbar - shows error without blocking the UI */}
      <Snackbar
        open={showErrorSnackbar && !!error}
        autoHideDuration={6000}
        onClose={handleCloseErrorSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseErrorSnackbar} severity="error" sx={{ width: '100%' }}>
          {t('Error')}: {error}
        </Alert>
      </Snackbar>
      {/* Header - Responsive */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {t('Customers')}
        </Typography>
        <Button
          variant="contained"
          data-testid="add-customer-button"
          onClick={() => {
            setSelectedCustomer(null);
            setAddModalOpen(true);
          }}
          startIcon={<AddIcon />}
          sx={{
            bgcolor: 'secondary.main',
            color: 'white',
            px: 2.5,
            py: 1.5,
            borderRadius: '10px',
            fontWeight: 500,
            fontSize: '14px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            boxShadow: '0 4px 12px rgba(49, 130, 206, 0.3)',
            '&:hover': {
              bgcolor: 'secondary.light',
              boxShadow: '0 6px 16px rgba(49, 130, 206, 0.4)',
            },
          }}
        >
          {t('Add Customer')}
        </Button>
      </Box>

      {/* Search and Filter Row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <SearchInput
          value={preferences.searchQuery || ''}
          onChange={(value) => updatePreferences({ searchQuery: value })}
          onClear={() => updatePreferences({ searchQuery: '' })}
          placeholder={t('Search by name, email, phone...')}
        />
        <FilterButton
          t={t}
          activeCount={activeFilterCount}
          isOpen={filtersOpen}
          onClick={toggleFiltersOpen}
        />
      </Box>

      {/* Active Filter Summary */}
      <FilterSummary chips={filterSummaryChips} onClear={resetFilters} t={t} />

      {/* Filter Panel */}
      <FilterPanel
        open={filtersOpen}
        resultCount={totalCount}
        onReset={resetFilters}
        resetDisabled={activeFilterCount === 0}
        t={t}
      >
        <FilterSection title={t('Customer Type')}>
          <FilterChip
            icon={<BusinessIcon />}
            label={t('Companies')}
            active={filters.type === 'company'}
            onClick={() => handleTypeFilter('company')}
            color="primary"
          />
          <FilterChip
            icon={<PersonIcon />}
            label={t('Individuals')}
            active={filters.type === 'individual'}
            onClick={() => handleTypeFilter('individual')}
            color="success"
          />
        </FilterSection>

        <FilterSection title={t('Contact Info')} dividerTop>
          <FilterChip
            icon={<EmailIcon />}
            label={t('Has email')}
            active={filters.has_email === true || filters.has_email === 'true'}
            onClick={() => handleEmailFilter(true)}
            color="primary"
          />
          <FilterChip
            icon={<EmailIcon />}
            label={t('No email')}
            active={filters.has_email === false || filters.has_email === 'false'}
            onClick={() => handleEmailFilter(false)}
            color="warning"
          />
          <FilterChip
            icon={<PhoneIcon />}
            label={t('Has phone')}
            active={filters.has_phone === true || filters.has_phone === 'true'}
            onClick={() => handlePhoneFilter(true)}
            color="primary"
          />
          <FilterChip
            icon={<PhoneIcon />}
            label={t('No phone')}
            active={filters.has_phone === false || filters.has_phone === 'false'}
            onClick={() => handlePhoneFilter(false)}
            color="warning"
          />
        </FilterSection>
      </FilterPanel>

      {/* Desktop Table */}
      <DataTable
        columns={[
          {
            key: 'type_icon',
            label: '',
            width: '1%',
            render: (customer) => (
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  bgcolor: customer.type === 'company' ? 'rgba(49, 130, 206, 0.1)' : 'rgba(72, 187, 120, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: customer.type === 'company' ? 'secondary.main' : 'success.main',
                }}
              >
                {customer.type === 'company' ? (
                  <BusinessIcon fontSize="small" />
                ) : (
                  <PersonIcon fontSize="small" />
                )}
              </Box>
            ),
          },
          {
            key: 'name',
            label: t('Name'),
            sortable: true,
            render: (customer) => (
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
                {customer.name}
              </Typography>
            ),
          },
          {
            key: 'email',
            label: t('Email'),
            sortable: true,
            render: (customer) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {customer.email || '-'}
              </Typography>
            ),
          },
          {
            key: 'phone',
            label: t('Phone'),
            sortable: true,
            render: (customer) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {customer.phone || '-'}
              </Typography>
            ),
          },
          {
            key: 'address',
            label: t('Address'),
            maxWidth: 200,
            render: (customer) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {customer.address_formatted || customer.address || '-'}
              </Typography>
            ),
          },
          {
            key: 'created_at',
            label: t('Created at'),
            sortable: true,
            width: '1%',
            noWrap: true,
            render: (customer) => (
              <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>
                {formatDate(customer.created_at)?.split(' ')[0]}
              </Typography>
            ),
          },
        ]}
        data={customers}
        loading={loading}
        onRowClick={(customer) => {
          setSelectedCustomerId(customer.id);
          setDetailsDialogOpen(true);
        }}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        emptyTitle={searchQuery || activeFilterCount > 0 ? t('No customers match your criteria') : t('No customers yet')}
        testIdPrefix="customer-row"
        tableTestId="customers-table"
        pagination={{
          page,
          totalPages,
          totalCount,
          pageSize,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        }}
      />

      {/* Mobile Cards - Hidden on desktop */}
      <Box sx={{ display: { xs: 'block', lg: 'none' }, position: 'relative' }}>
        {/* Loading overlay for refetch */}
        {loading && customers.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              pt: 8,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {loading && customers.length === 0 ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : customers.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {searchQuery || activeFilterCount > 0 ? t('No customers match your criteria') : t('No customers yet')}
            </Typography>
          </Paper>
        ) : (
          <>
            {customers.map((customer) => (
              <Paper
                key={customer.id}
                data-testid={`customer-row-${customer.id}`}
                elevation={0}
                onClick={() => {
                  setSelectedCustomerId(customer.id);
                  setDetailsDialogOpen(true);
                }}
                sx={{
                  p: 2,
                  mb: 1.5,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {/* Type Icon */}
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: customer.type === 'company' ? 'rgba(49, 130, 206, 0.1)' : 'rgba(72, 187, 120, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: customer.type === 'company' ? 'secondary.main' : 'success.main',
                      flexShrink: 0,
                    }}
                  >
                    {customer.type === 'company' ? (
                      <BusinessIcon />
                    ) : (
                      <PersonIcon />
                    )}
                  </Box>

                  {/* Customer Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: 'text.primary',
                        mb: 0.5,
                      }}
                    >
                      {customer.name}
                    </Typography>

                    {/* Contact Info */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {customer.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          <Typography
                            sx={{
                              fontSize: '13px',
                              color: 'text.secondary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {customer.email}
                          </Typography>
                        </Box>
                      )}
                      {customer.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                            {customer.phone}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            ))}

            {/* Mobile Pagination Controls */}
            <PaginationControls
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              loading={loading}
            />
          </>
        )}
      </Box>

      {/* Add Customer Dialog - only for new customers */}
      <CustomerDialog
        open={addModalOpen}
        addCreatedCustomer={addCreatedCustomer}
        updateCustomer={updateCustomer}
        handleClose={() => setAddModalOpen(false)}
        customer={selectedCustomer}
        onDelete={handleDeleteCustomer}
      />

      {/* Customer Details Dialog - for viewing/editing existing customers */}
      <CustomerDetailsDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        customerId={selectedCustomerId}
        onCustomerUpdated={(updatedCustomer) => {
          if (updatedCustomer) {
            refetch();
          } else {
            // Customer was deleted
            refetch();
          }
        }}
      />
    </Box>
  );
};

export default withTranslation()(MainCustomersView);
